import { useState } from 'react'
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  Button,
  Input,
  Spinner,
  Tooltip,
  Typography,
} from '@material-tailwind/react'
import { BiCurrentLocation } from 'react-icons/bi'
import { PiLightbulbFilamentLight } from 'react-icons/pi'
import { useTranslation } from 'react-i18next'
import { AccordionCaret } from '../components/AccordionCaret'
import { useGeneralToast } from '../hooks/toast'
import {
  MAX_SPOT_DEADBAND,
  MAX_SPOT_RADIUS,
  MIN_SPOT_DEADBAND,
  MIN_SPOT_RADIUS,
} from '../utils/constants/geoxp.constants'
import { getRandomSpot } from '../utils/helpers/geoxp.helpers'
import { getLocationAndReverseGeocoding } from '../utils/helpers/mapbox.helpers'
import { validateInputNumber } from '../utils/helpers/validate.helpers'
import PositionSuggestion from './PositionSuggestion'
import { useDarkMode } from '../contexts/DarkModeContext'
import { AgamiUploadFormData } from './UploadAgamiInterface'

interface SpotPositionSelectorProps {
  formData: AgamiUploadFormData
  setFormData: (data: AgamiUploadFormData | ((prev: AgamiUploadFormData) => AgamiUploadFormData)) => void
  uploading: boolean
  handleDataChange: <K extends keyof AgamiUploadFormData>(key: K, newValue: AgamiUploadFormData[K]) => void
  checkInputIsValid: <K extends keyof AgamiUploadFormData>(
    key: K,
    condition: boolean,
    fallbackVal?: AgamiUploadFormData[K] | undefined
  ) => void
}

const SpotPositionSelector: React.FC<SpotPositionSelectorProps> = ({
  formData,
  setFormData,
  uploading,
  handleDataChange,
  checkInputIsValid,
}) => {
  const { t } = useTranslation()
  const [suggestionInput, setSuggestionInput] = useState('')
  const [loadingLocation, setLoadingLocation] = useState(false)
  const [openAdvSettings, setOpenAdvSettings] = useState(false)
  const { error: toastError } = useGeneralToast()
  const { darkMode } = useDarkMode()

  const onSuggestionSelect = (value: { center: [number, number] }) => {
    handleDataChange('longitude', value?.center[0])
    handleDataChange('latitude', value?.center[1])
  }

  const onNeedInspiration = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const spot = getRandomSpot() as {
      latitude: number
      longitude: number
      radius: number
      deadband: number
      label: string
    }
    const { latitude, longitude, radius, deadband, label } = spot
    setSuggestionInput(label)
    // TODO check
    // handleDataChange();
    setFormData((current) => ({
      ...current,
      latitude,
      longitude,
      radius,
      deadband,
    }))
  }

  const onCurrentLocation = async (e: React.MouseEvent) => {
    setLoadingLocation(true)
    e.preventDefault()
    e.stopPropagation()
    const result = (await getLocationAndReverseGeocoding()) as
      | { latitude: number; longitude: number; radius: number; label: string }
      | { error: Error }
    if ('error' in result) {
      toastError(t('upload.position.errorGettingLocation'))
      setLoadingLocation(false)
      return
    }
    const { latitude, longitude, radius, label } = result
    setSuggestionInput(label)
    setFormData((current) => ({
      ...current,
      latitude,
      longitude,
      radius,
    }))
    setLoadingLocation(false)
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <Typography className="font-bold text-primary text-lg font-barlow" data-testid="agami-geo-pos-title">
          {t('upload.position.title')}
        </Typography>

        <div className="flex items-center space-x-2">
          <Tooltip content={t('upload.position.needInspiration')}>
            <Button
              type="button"
              size="sm"
              onClick={onNeedInspiration}
              disabled={loadingLocation || uploading}
            >
              <div className="flex gap-2 items-center justify-center">
                <PiLightbulbFilamentLight className="w-4 h-4" /> ?
              </div>
            </Button>
          </Tooltip>
        </div>
      </div>

      <div className="w-full flex gap-2">
        <PositionSuggestion
          suggestionInput={suggestionInput}
          setSuggestionInput={setSuggestionInput}
          onSuggestionSelect={onSuggestionSelect}
        />
        <Tooltip content={t('upload.position.useCurrentPosition')}>
          <Button color={darkMode ? 'white' : 'gray'} variant="outlined" onClick={onCurrentLocation}>
            {loadingLocation ? (
              <Spinner className="h-4 w-4" />
            ) : (
              <BiCurrentLocation className="text-blue-gray-500 dark:text-base-gray-500" />
            )}
          </Button>
        </Tooltip>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-2">
        <Input
          className="w-full"
          value={formData.latitude}
          onChange={(e) => handleDataChange('latitude', validateInputNumber(e.currentTarget.value))}
          type="number"
          step="0.001"
          label={t('upload.position.latitude')}
          required
          disabled={uploading}
          data-testid="agami-geo-pos-lat-input"
          {...(darkMode && { color: 'white' })}
        />
        <Input
          className="w-full"
          value={formData.longitude}
          onChange={(e) => handleDataChange('longitude', validateInputNumber(e.currentTarget.value ?? ''))}
          type="number"
          step="0.001"
          label={t('upload.position.longitude')}
          required
          disabled={uploading}
          data-testid="agami-geo-pos-lon-input"
          {...(darkMode && { color: 'white' })}
        />
      </div>
      <Input
        className="w-full"
        value={formData.radius}
        onChange={(e) => handleDataChange('radius', validateInputNumber(e.currentTarget.value ?? ''))}
        onBlur={() => {
          checkInputIsValid('radius', (formData.radius ?? 0) > MIN_SPOT_RADIUS, MIN_SPOT_RADIUS)
          checkInputIsValid('radius', (formData.radius ?? 0) < MAX_SPOT_RADIUS, MAX_SPOT_RADIUS)
        }}
        type="number"
        min={MIN_SPOT_RADIUS}
        max={MAX_SPOT_RADIUS}
        label={t('upload.position.radiusLabel')}
        required
        disabled={uploading}
        data-testid="agami-geo-pos-radius-input"
        {...(darkMode && { color: 'white' })}
      />
      <Typography variant="small" className="mt-[-8px]">
        {t('upload.position.minRadius')}: <em className="font-bold">{MIN_SPOT_RADIUS} m</em> /{' '}
        {t('upload.position.maxRadius')}: <em className="font-bold">{MAX_SPOT_RADIUS} m</em>
      </Typography>
      <Accordion open={openAdvSettings} icon={<AccordionCaret rotate={openAdvSettings} />}>
        <AccordionHeader
          onClick={() => setOpenAdvSettings((prev) => !prev)}
          className="justify-center gap-4 text-md dark:text-gray-200"
        >
          {t('upload.position.advancedSettings')}
        </AccordionHeader>
        <AccordionBody>
          <Input
            className="w-full"
            value={formData.deadband}
            onChange={(e) => handleDataChange('deadband', validateInputNumber(e.currentTarget.value ?? ''))}
            onBlur={() => {
              if (formData.deadband === undefined) return
              const deadbandNum = Number(formData.deadband)
              checkInputIsValid('deadband', deadbandNum > MIN_SPOT_DEADBAND, MIN_SPOT_DEADBAND)
              checkInputIsValid('deadband', deadbandNum < MAX_SPOT_DEADBAND, MAX_SPOT_DEADBAND)
            }}
            type="number"
            min={MIN_SPOT_DEADBAND}
            max={MAX_SPOT_DEADBAND}
            label={t('upload.position.deadbandLabel')}
            disabled={uploading}
            {...(darkMode && { color: 'white' })}
          />
          <Typography variant="small">{t('upload.position.deadbandDescription')}</Typography>
        </AccordionBody>
      </Accordion>
    </div>
  )
}

export default SpotPositionSelector
